// /src/components/ComparisonTable.jsx
// Tabella comparativa Sfarinà vs concorrenti.
// Desktop (lg+): 4 colonne side-by-side
// Mobile (< lg): card stack verticale, una card per riga di caratteristica.

import React from 'react';
import { Check, X, Star } from 'lucide-react';
import { useT } from '../i18n/LanguageContext';

const ComparisonTable = () => {
  const { t } = useT();
  const { compare } = t;

  return (
    <section className="py-20 md:py-24 bg-[#F7F3EC]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 max-w-2xl mx-auto">
          <p className="font-['Poppins',_sans-serif] uppercase text-xs tracking-[0.3em] text-[#b03121] mb-4">
            {compare.subtitle}
          </p>
          <h2 className="font-['Poppins',_sans-serif] font-bold text-3xl md:text-4xl lg:text-5xl text-[#1A1A1A] !leading-tight">
            {compare.title}
          </h2>
        </div>

        {/* ============ DESKTOP TABLE (lg+) ============ */}
        <div className="hidden lg:block overflow-x-auto">
          <div className="flex w-full pt-8 items-stretch gap-2">
            {/* Colonna label */}
            <div className="w-[18%] flex flex-col">
              <div className="h-24 flex items-end pb-3 px-2">
                <span className="opacity-0 text-sm">Header</span>
              </div>
              {compare.rows.map((row, i) => (
                <div
                  key={row.key}
                  className={`min-h-[140px] flex items-center font-['Poppins',_sans-serif] font-bold text-sm text-[#1A1A1A]/80 px-4 ${
                    i % 2 === 1 ? 'bg-[#EDE8E0]/40' : ''
                  } border-b border-[#EDE8E0]`}
                >
                  {row.label}
                </div>
              ))}
            </div>

            {/* Colonna 2 — Supermercato */}
            <div className="w-[24%] flex flex-col px-1 opacity-70 hover:opacity-100 transition-opacity">
              <div className="h-24 flex items-end justify-center pb-3">
                <h4 className="font-['Poppins',_sans-serif] font-semibold text-center text-[#1A1A1A] text-sm leading-snug">
                  {compare.cols.super.line1}
                  <br />
                  {compare.cols.super.line2}
                </h4>
              </div>
              {compare.rows.map((row, i) => (
                <div
                  key={row.key}
                  className={`min-h-[140px] flex items-center justify-center text-center font-['Lora',_serif] text-sm text-[#5a4a42] px-3 ${
                    i % 2 === 1 ? 'bg-[#EDE8E0]/40' : ''
                  } border-b border-[#EDE8E0]`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <X size={20} className="text-[#1A1A1A]/30 shrink-0" />
                    <span>{row.super}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Colonna 3 — SFARINÀ (highlight) */}
            <div className="w-[34%] flex flex-col bg-[#b03121] rounded-2xl shadow-2xl border border-[#8e261a] relative -mt-2 z-10">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#1A1A1A] text-white font-['Poppins',_sans-serif] font-bold text-[10px] px-4 py-1 rounded-full tracking-widest uppercase shadow-lg whitespace-nowrap">
                {compare.best_choice}
              </div>
              <div className="h-24 flex items-end justify-center pb-3 pt-2">
                <h4 className="font-['Poppins',_sans-serif] font-black text-center text-white text-2xl tracking-tight">
                  {compare.cols.sfarina}
                </h4>
              </div>
              {compare.rows.map((row, i) => (
                <div
                  key={row.key}
                  className={`min-h-[140px] flex items-center justify-center text-center font-['Lora',_serif] text-white font-medium px-5 ${
                    i % 2 === 1 ? 'bg-white/10' : ''
                  } ${i === compare.rows.length - 1 ? 'rounded-b-2xl' : 'border-b border-white/20'}`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <Check size={22} className="text-white shrink-0" />
                    <span>{row.sfarina}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Colonna 4 — Acchiappa Turisti */}
            <div className="w-[24%] flex flex-col px-1 opacity-70 hover:opacity-100 transition-opacity">
              <div className="h-24 flex items-end justify-center pb-3">
                <h4 className="font-['Poppins',_sans-serif] font-semibold text-center text-[#1A1A1A] text-sm leading-snug">
                  {compare.cols.tourist.line1}
                  <br />
                  {compare.cols.tourist.line2}
                </h4>
              </div>
              {compare.rows.map((row, i) => (
                <div
                  key={row.key}
                  className={`min-h-[140px] flex items-center justify-center text-center font-['Lora',_serif] text-sm text-[#5a4a42] px-3 ${
                    i % 2 === 1 ? 'bg-[#EDE8E0]/40' : ''
                  } border-b border-[#EDE8E0]`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <X size={20} className="text-[#1A1A1A]/30 shrink-0" />
                    <span>{row.tourist}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ============ MOBILE STACK (< lg) ============ */}
        <div className="lg:hidden space-y-6">
          {compare.rows.map((row) => (
            <div
              key={row.key}
              className="bg-white rounded-2xl shadow-md border border-[#EDE8E0] overflow-hidden"
              data-testid={`compare-row-${row.key}`}
            >
              <div className="bg-[#1A1A1A] text-white font-['Poppins',_sans-serif] font-bold text-sm uppercase tracking-wider px-5 py-3">
                {row.label}
              </div>
              <div className="divide-y divide-[#EDE8E0]">
                {/* SFARINÀ first (highlight) */}
                <div className="bg-[#b03121] text-white px-5 py-4 flex items-start gap-3 relative">
                  <div className="absolute top-3 right-4 bg-[#1A1A1A] text-white font-['Poppins',_sans-serif] font-bold text-[9px] px-2.5 py-0.5 rounded-full tracking-widest uppercase">
                    <Star size={10} className="inline -mt-0.5 mr-1" />
                    {compare.best_choice}
                  </div>
                  <Check size={20} className="text-white shrink-0 mt-1" />
                  <div>
                    <p className="font-['Poppins',_sans-serif] font-black text-base tracking-tight mb-1">
                      {compare.cols.sfarina}
                    </p>
                    <p className="font-['Lora',_serif] text-sm leading-snug">{row.sfarina}</p>
                  </div>
                </div>

                <div className="px-5 py-4 flex items-start gap-3 opacity-80">
                  <X size={18} className="text-[#1A1A1A]/30 shrink-0 mt-1" />
                  <div>
                    <p className="font-['Poppins',_sans-serif] font-semibold text-xs text-[#1A1A1A]/70 mb-1">
                      {compare.cols.super.line1} {compare.cols.super.line2}
                    </p>
                    <p className="font-['Lora',_serif] text-sm text-[#5a4a42] leading-snug">{row.super}</p>
                  </div>
                </div>

                <div className="px-5 py-4 flex items-start gap-3 opacity-80">
                  <X size={18} className="text-[#1A1A1A]/30 shrink-0 mt-1" />
                  <div>
                    <p className="font-['Poppins',_sans-serif] font-semibold text-xs text-[#1A1A1A]/70 mb-1">
                      {compare.cols.tourist.line1} {compare.cols.tourist.line2}
                    </p>
                    <p className="font-['Lora',_serif] text-sm text-[#5a4a42] leading-snug">{row.tourist}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ComparisonTable;
